// Application shell. Probes the session, gates the app behind login, and — once
// authenticated — starts the live feed and renders the console: sidebar, header, banners,
// the active page, the inspector drawer, the narrow bottom-sheet, and toasts.

import { useEffect, useState } from 'react';
import { BootSplash } from './components/BootSplash';
import { LoginScreen } from './auth/LoginScreen';
import { RegisterPage } from './auth/RegisterPage';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { Banners } from './components/Banners';
import { Drawer } from './components/Drawer';
import { BottomSheet } from './components/BottomSheet';
import { Toasts } from './components/Toasts';
import { Overview } from './pages/Overview';
import { TradingVps } from './pages/TradingVps';
import { Accounts } from './pages/Accounts';
import { Positions } from './pages/Positions';
import { Alerts } from './pages/Alerts';
import { VpsTerminals } from './pages/VpsTerminals';
import { AccountDetail } from './pages/AccountDetail';
import { Settings } from './pages/Settings';
import { Docs } from './pages/Docs';
import { C } from './lib/tokens';
import { PBTN } from './lib/styles';
import { focusSearch } from './components/searchRef';
import { useAuth, useData, useUi } from './store/hooks';
import { checkSession, markSessionExpired } from './store/authStore';
import { feed } from './store/dataStore';
import { uiStore, closeDrawer, type NavKey, EMPTY_FILTERS } from './store/uiStore';

// Wire the feed's auth-expiry hook once.
feed.onAuthExpired = markSessionExpired;

// ---- URL hash <-> filter state (shareable views, like the mockup) ---------------------

function readHash(): void {
  try {
    const h = location.hash.slice(1);
    if (!h) return;
    const o = JSON.parse(atob(decodeURIComponent(h))) as {
      n?: NavKey;
      q?: string;
      k?: Record<string, boolean>;
      f?: Partial<typeof EMPTY_FILTERS>;
      s?: { k: string; d: number };
    };
    uiStore.set({
      nav: o.n ?? 'overview',
      q: o.q ?? '',
      quick: o.k ?? {},
      f: { ...EMPTY_FILTERS, ...(o.f ?? {}) },
      sort: o.s ?? { k: 'sev', d: 1 },
    });
  } catch {
    /* malformed hash — ignore */
  }
}

function writeHash(): void {
  const s = uiStore.get();
  try {
    history.replaceState(null, '', '#' + encodeURIComponent(btoa(JSON.stringify({ n: s.nav, q: s.q, k: s.quick, f: s.f, s: s.sort }))));
  } catch {
    /* ignore */
  }
}

function Page({ nav }: { nav: NavKey }) {
  switch (nav) {
    case 'overview':
      return <Overview />;
    case 'tradingVps':
      return <TradingVps />;
    case 'accounts':
      return <Accounts />;
    case 'positions':
      return <Positions />;
    case 'alerts':
      return <Alerts />;
    case 'vps':
      return <VpsTerminals />;
    case 'detail':
      return <AccountDetail />;
    case 'settings':
      return <Settings />;
    case 'docs':
      return <Docs />;
    default:
      return <Overview />;
  }
}

function Console() {
  const nav = useUi((s) => s.nav);
  const narrow = useUi((s) => s.narrow);
  const drawerOpen = useUi((s) => s.drawerId != null);
  const loaded = useData((s) => s.loaded);
  const error = useData((s) => s.error);

  // Start / stop the live feed with the console's lifetime.
  useEffect(() => {
    void feed.start();
    return () => feed.stop();
  }, []);

  if (!loaded && error) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Cannot reach the monitoring backend</div>
          <div style={{ fontSize: 12, color: C.dm, marginBottom: 16, lineHeight: 1.5 }}>{error}</div>
          <button onClick={() => void feed.resnapshot()} style={PBTN}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return <BootSplash message="Connecting to monitoring backend…" />;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {!narrow && <Sidebar />}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
        <Header />
        <Banners />
        <main id="main" style={{ flex: 1, overflow: 'auto', padding: '14px 16px', minWidth: 0 }}>
          <Page nav={nav} />
        </main>
      </div>
      {drawerOpen && <Drawer />}
      <BottomSheet />
      <Toasts />
    </div>
  );
}

export default function App() {
  const status = useAuth((s) => s.status);
  // Track the URL path so the public /register route can render pre-auth and we can
  // route back to the login screen after a successful registration.
  const [route, setRoute] = useState<string>(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // One-time boot: read the shareable hash, probe the session, set up global listeners.
  useEffect(() => {
    readHash();
    void checkSession();

    const mq = window.matchMedia('(max-width: 920px)');
    const applyNarrow = () => uiStore.set({ narrow: mq.matches });
    applyNarrow();
    mq.addEventListener('change', applyNarrow);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDrawer();
        uiStore.set({ colMenu: false, sheetOpen: false, saveOpen: false });
      } else if (e.key === '/') {
        const tag = (document.activeElement && document.activeElement.tagName) || '';
        if (!/INPUT|SELECT|TEXTAREA/.test(tag)) {
          e.preventDefault();
          focusSearch();
        }
      }
    };
    window.addEventListener('keydown', onKey);

    // Mirror nav/filters/sort to the URL hash.
    let last = '';
    const unsub = uiStore.subscribe(() => {
      const s = uiStore.get();
      const key = JSON.stringify({ n: s.nav, q: s.q, k: s.quick, f: s.f, s: s.sort });
      if (key !== last) {
        last = key;
        writeHash();
      }
    });

    return () => {
      mq.removeEventListener('change', applyNarrow);
      window.removeEventListener('keydown', onKey);
      unsub();
    };
  }, []);

  // Public self-service registration — reachable WITHOUT a session, even while the session
  // probe is still in flight. On completion, clear the token from the URL and fall through
  // to the normal auth flow (which shows the login screen).
  if (route === '/register') {
    return (
      <RegisterPage
        onComplete={() => {
          window.history.replaceState({}, '', '/');
          setRoute('/');
        }}
      />
    );
  }

  if (status === 'loading') return <BootSplash message="Checking session…" />;
  if (status === 'anonymous') return <LoginScreen />;
  return <Console />;
}
